import { FormattedFloatInput } from "@msys/ui";
import { Functions as FunctionsIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import React from "react";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { AttributeExpressionModal } from "../../doc-items/modals/AttributeExpressionModal.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  initialQuantity: number;
  // The existing `estimatedQuantity` expression (when one is set on the item),
  // so the AttributeExpressionModal opens with it pre-filled and the Delete
  // action is enabled — mirroring `DataGridFloatWithExpressionDisplay` and
  // `FormattedFloatWithExpressionField`.
  expression: string | undefined;
  // Called once on commit (blur / Enter) with the new value.
  onCommit: (estimatedQuantity: number) => Promise<void> | void;
  // Called after the formula modal saves / deletes an expression. The modal's
  // mutation bypasses `onCommit`, and Apollo cache normalization is inert on
  // `fetchPolicy: "no-cache"` preview routes, so the parent must refetch here
  // to reflect the new / removed formula.
  onExpressionChange?: () => Promise<void> | void;
  // Exits the surrounding InlineEditableCell's edit mode.
  handleBlur: () => void;
}

export function QuantityInlineEditor({
  projectId,
  docId,
  itemId,
  initialQuantity,
  expression,
  onCommit,
  onExpressionChange,
  handleBlur,
}: Props) {
  const [value, setValue] = React.useState<number | null>(initialQuantity);
  const valueRef = React.useRef<number | null>(initialQuantity);

  const [isExpressionModalOpen, setExpressionModalOpen] = React.useState(false);
  const expressionButtonRef = React.useRef<HTMLButtonElement>(null);
  const sealedRef = React.useRef(false);

  const commit = async () => {
    if (sealedRef.current) return;
    sealedRef.current = true;
    const next = valueRef.current;
    try {
      if (next !== null && next !== initialQuantity) await onCommit(next);
    } catch (error) {
      // Commit failed — unseal and stay in edit mode so the user can retry or
      // Escape-discard. Swallowing here also prevents an unhandled rejection
      // (callers fire-and-forget via `void commit()`).
      sealedRef.current = false;
      console.error("quantity commit failed", error);
      return;
    }
    handleBlur();
  };

  const discard = () => {
    if (sealedRef.current) return;
    sealedRef.current = true;
    handleBlur();
  };

  // When the field already carries a formula, clicking to edit MUST go
  // straight to the formula modal — typing a literal would silently
  // overwrite the expression. Mirrors `FormattedFloatWithExpressionField`'s
  // click handler. There is no literal input to focus; closing the modal
  // exits edit mode via `discard()`.
  if (expression !== undefined) {
    return (
      <RestrictedByCapabilityWithDebug
        capability="expressions"
        otherwise={<ExitOnMount onExit={discard} />}
      >
        <AttributeExpressionModal
          projectId={projectId}
          docId={docId}
          itemId={itemId}
          fieldName="estimatedQuantity"
          expression={expression}
          handleClose={discard}
          handleComplete={async (_expression, handleClose) => {
            // `AttributeExpressionModal` fires this without awaiting, so a
            // rejected refetch must not become an unhandled rejection — and the
            // modal must always close, even when the refetch fails.
            try {
              await onExpressionChange?.();
            } catch (error) {
              console.error("refetch after expression change failed", error);
            } finally {
              handleClose();
            }
          }}
        />
      </RestrictedByCapabilityWithDebug>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", width: "100%" }}
    >
      <FormattedFloatInput
        size="extra-small"
        variant="outlined"
        autoFocus
        fullWidth
        debounceMs={0}
        // Non-negative guard — matches the `min(0)` validation on the other
        // estimated-quantity editors (e.g. `EstimatedCalculationBox`). A value
        // below `min` throws inside the input's parse step, so `onChange`
        // never delivers it and the field shows the built-in error text.
        min={0}
        value={value}
        onChange={newValue => {
          valueRef.current = newValue;
          setValue(newValue);
        }}
        onBlur={event => {
          if (event.relatedTarget === expressionButtonRef.current) return;
          void commit();
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            discard();
          }
        }}
      />
      <RestrictedByCapabilityWithDebug capability="expressions">
        <IconButton
          size="extra-small"
          color="secondary"
          onClick={() => setExpressionModalOpen(true)}
          ref={expressionButtonRef}
        >
          <FunctionsIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </RestrictedByCapabilityWithDebug>
      {isExpressionModalOpen && (
        <AttributeExpressionModal
          projectId={projectId}
          docId={docId}
          itemId={itemId}
          fieldName="estimatedQuantity"
          expression={undefined}
          handleClose={() => {
            setExpressionModalOpen(false);
            discard();
          }}
          handleComplete={async (_expression, handleClose) => {
            // Same as the existing-expression path above: never leave the
            // modal open on a failed refetch, never reject unhandled.
            try {
              await onExpressionChange?.();
            } catch (error) {
              console.error("refetch after expression change failed", error);
            } finally {
              handleClose();
            }
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Runs `onExit` exactly once on mount. Used to exit the surrounding cell's
 * edit mode when a capability-less org reaches a formula cell — the formula
 * editor must not open, so the cell reverts to its read-only display.
 */
function ExitOnMount({ onExit }: { onExit: () => void }) {
  const onExitRef = React.useRef(onExit);
  onExitRef.current = onExit;
  React.useEffect(() => {
    onExitRef.current();
  }, []);
  return null;
}
